import BackToListing from '@components/BackToListing';
import CustomCard from '@components/CustomCard';
import useSwr from '@hooks/useSwr';
import { Button, Col, Descriptions, Row, Tooltip } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CustomTag from '@components/CustomTag';
import { SharedPlanPayload } from '@pages/Administrative/SharedPlans/NewSharedPlanModal';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import Inventory from '@pages/Assets/Inventory';
import Transfers from '@pages/Operational/Transfers';
import { bytesFormat } from '@utils/formatters';
import { GroupingPayload } from '../NewGroupingModal';

interface GroupingProps extends GroupingPayload {
  department: {
    id: number;
    name: string;
  };
  sharingPlan: SharedPlanPayload;
}

const GroupingDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['groupings', 'attributes']);
  const { groupingId } = useParams();

  const { data } = useSwr<GroupingProps>(
    groupingId ? `/service-proxy/broker/inventory-groups/${groupingId}` : null,
  );

  const sharedPlan = useMemo(() => data?.sharingPlan, [data?.sharingPlan]);

  const activatedTag = useCallback(
    (value: boolean | undefined) => (
      <CustomTag color={value ? 'green' : 'red'}>
        {t(
          `attributes:automaticBlocking.${value ? 'activated' : 'disabled'}`,
        ).toUpperCase()}
      </CustomTag>
    ),
    [t],
  );

  const groupingItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:description'),
        value: data?.name,
        showItem: !!data?.name,
      },
      {
        label: t('attributes:sharedPlan'),
        value: (
          <Tooltip title={t('msgs.viewSharedPlan')}>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                navigate(
                  `/${ApplicationsConstants.MOT_BUSINESS}/mobile-network/administrative/shared-plans/${sharedPlan?.id}`,
                );
              }}
            >
              {`${sharedPlan?.id} - ${sharedPlan?.description}`}
            </Button>
          </Tooltip>
        ),
        showItem: !!sharedPlan?.description,
      },
      {
        label: t('attributes:department'),
        value: data?.department?.name,
        showItem: !!data?.department?.name,
      },
      {
        label: t('attributes:franchise'),
        value: bytesFormat(data?.franchiseBytes),
        showItem: !!data?.franchiseBytes,
      },
      {
        label: t('attributes:automaticBlocking.title'),
        value: activatedTag(data?.automaticBlocking),
        showItem: true,
      },
    ],
    [
      t,
      data?.name,
      data?.franchiseBytes,
      data?.automaticBlocking,
      data?.department?.name,
      activatedTag,
      sharedPlan?.id,
      sharedPlan?.description,
      navigate,
    ],
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <CustomCard
          title={
            <BackToListing style={{ margin: 0 }} title={t('groupingDetails')} />
          }
          loading={!data}
        >
          <Descriptions
            column={{ lg: 2, md: 2, sm: 2, xs: 1 }}
            style={{ marginTop: 24, padding: '0 16px' }}
          >
            {groupingItemsDescriptions?.slice(0, 2)?.map(
              ({ label, value, showItem }) =>
                value &&
                showItem && (
                  <Descriptions.Item key={label} label={label}>
                    {value}
                  </Descriptions.Item>
                ),
            )}
          </Descriptions>
          <Descriptions
            column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
            style={{ padding: '0 16px' }}
          >
            {groupingItemsDescriptions?.slice(2)?.map(
              ({ label, value, showItem }) =>
                value &&
                showItem && (
                  <Descriptions.Item key={label} label={label}>
                    {value}
                  </Descriptions.Item>
                ),
            )}
          </Descriptions>
        </CustomCard>
      </Col>
      <Col style={{ marginTop: 16 }}>
        <Transfers
          isInTheRequestDetails
          defaultFilters={{
            virtualAccountId: data?.id,
          }}
          columnsToRemove={['oldCustomerName', 'newCustomerName', 'request']}
        />
      </Col>

      <Col span={24}>
        <Inventory
          tableOnly
          defaultFilters={{
            accountId: data?.id,
            showContracted: 'false',
          }}
          defaultColumns={[
            'actions',
            'online',
            'msisdn',
            'iccid',
            'status',
            'activationDate',
          ]}
        />
      </Col>
    </Row>
  );
};

export default GroupingDetails;
